// Translation dictionary for the paywall + Stripe-checkout mini-localization
// project (Vietnamese + Brazilian Portuguese). English is the source of truth
// and the fallback; every other locale mirrors its key structure.
//
// Keys are namespaced like i18next (e.g. "billing.subscribe") so a future
// migration to a real i18n library (see LOCALIZATION_SPEC.md) is mechanical.
//
// NOTE: Tarot card names (e.g. "The Fool") always stay in English and are never
// added here.

import type { PaywallLocale } from "./locale";

// Recursive string tree — leaves are the translatable strings.
type StringTree = { [key: string]: string | StringTree };

// Translations live in sibling files so each stays readable. The type-only
// export/import cycle (StringDict) is erased at compile time, so this is safe.
import { vi } from "./strings.vi";
import { ptBR } from "./strings.pt-BR";

const en = {
  billing: {
    heading: "Use Moonlight anytime!",
    home: "Home",
    contactUs: "Contact us",
    toggleMonthly: "Monthly",
    toggleAnnual: "Annual",
    monthUnit: "month",
    billedAnnually: "billed annually at {price}",
    subscribedThroughApple: "Subscribed through Apple",
    subscribedThroughGoogle: "Subscribed through Google Play",
    youreSubscribed: "You're subscribed",
    renews: "Renews",
    expires: "Expires",
    managePlan: "Manage plan",
    tryItFree: "Try it free",
    subscribe: "Subscribe",
    legacyProPlan: "you're on a legacy professional plan",
    extendTrialPrompt: "Want to extend your free trial?",
    sendANote: "Send a note",
    featureList: {
      unlimited: "Unlimited tarot readings & games",
      keywords: "Card keywords, personalized reading hints, and tarot tips",
      guidebook: "Expanded guidebook sections",
      journaling: "Journaling + interpretation help",
      spreads: "100+ spreads or craft your own",
      friends: "Read with friends",
      decks: "Includes 6 decks",
    },
    featuresHeading:
      "Moonlight is intuitive, easy-to-use, and made for your real life.",
    features: {
      meaningsHeader: "Understand card meanings",
      meaningsText:
        "Instant keywords & personalized guidebook hints for any question life throws your way.",
      anywhereHeader: "Read anywhere",
      anywhereText:
        "Access beautiful tarot spreads on mobile or web, whenever inspiration strikes.",
      spreadsHeader: "Custom spreads",
      spreadsText:
        "Choose from 100+ designed layouts or craft your own for specific questions.",
      deckArtHeader: "Explore deck art",
      deckArtText:
        "Transform your readings with different deck styles made by indie artists.",
      friendsHeader: "Read with friends",
      friendsText:
        "Deepen your solo practice or invite friends for live collaborative sessions.",
      practiceHeader: "Build a daily practice",
      practiceText:
        "Learn tarot with interactive puzzles, spreads, and journaling prompts.",
    },
    trial: {
      extended: "extended free trial, no automatic charge",
      hourLeft: "1 hour left in free trial",
      hoursLeft: "{count} hours left in free trial",
      dayLeft: "1 day left in free trial",
      daysLeft: "{count} days left in free trial",
    },
  },
  overlay: {
    titleMonthly: "Moonlight Monthly Subscription",
    titleAnnual: "Moonlight Annual Subscription",
    autoRenews: "Auto-renews until cancelled.",
    perMonth: "month",
    perYear: "year",
    loadError: "Couldn't load pricing. Please check your connection and try again.",
    retry: "Try again",
  },
  checkout: {
    processing: "Processing",
    confirm: "Confirm",
    payNow: "Pay now",
    sendRequest: "Send request",
    offerCode: "offer code",
    checking: "checking...",
    applied: "applied ✓",
    apply: "apply",
    remove: "remove",
    enterOfferCode: "Please enter an offer code",
    invalidCode: "Invalid code",
    errorValidating: "Error validating code. Try again.",
    percentOff: "{percent}% off",
    amountOff: "{amount} off",
    codeApplied: "Code applied",
    oneTimeUse: " (one-time use)",
    recurringDiscount: " (recurring discount)",
    validForMonth: " (valid for {count} month)",
    validForMonths: " (valid for {count} months)",
  },
  cta: {
    leftInTrial: "{time} left in free trial",
    keepGuidebook: "Keep the full guidebook with a",
    subscriptionLink: "Moonlight subscription",
    unlockGuidebook: "Unlock the full guidebook with a",
    tryGuidebook: "Try the full guidebook",
    hour: "1 hour",
    hours: "{count} hours",
    day: "1 day",
    days: "{count} days",
  },
  disclaimer: {
    securePaymentsStripe: "Secure payments powered by Stripe.",
    securePayments: "Secure payments.",
    chargedAfterConfirmed: "You're only charged after session is confirmed.",
    flexibleRefunds: "Flexible refunds.",
    moneyBack: "Get money back for cancellations.",
  },
  faq: {
    q1: "What's included in my subscription?",
    a1: "Your subscription includes unlimited self-hosted rooms, access to all spread layouts, 4 free decks with meanings, custom themes, lightweight notes, journaling, solo and group sessions, and multi-platform support (iOS, Android, and web).",
    q2: "What tarot decks will I get?",
    a2: "You get 4+ free decks, including: Rider Waite Smith, Marseille, Summer of Bibliomancy, and Internet Lore Tarot. All the other decks are available separately in our marketplace. That way each artist gets paid! You will still need to be a member to use artist decks.",
    q3: "Is there a free trial available?",
    a3: "Yes! We offer a 3-day free trial period for new folks to try it out. If you'd like to extend your trial, just <a href='mailto:support@moonlight.world'>send a note</a>.",
    q4: "Can I cancel my subscription?",
    a4: "Yes, you can cancel anytime. Your subscription will remain active until the end of your billing period.",
    q5: "Can I use Moonlight on my phone?",
    a5: "Absolutely! Moonlight works on both mobile devices and desktops, giving you flexibility to do readings anywhere. We have an iOS and Android app.",
    q6: "Will my guests need to subscribe to join my room?",
    a6: "Nope! Anyone joining your room as a guest (friends, clients, etc.) can be there free! They only need to subscribe if they want to do their own readings.",
    q7: "Do I need to subscribe to book a professional reading?",
    a7: "Nope! If you're booking with professionals on Moonlight, those sessions are paid for separately. The subscription is only for using our self-hosted rooms.",
    q8: "Does a subscription include professional booking software?",
    a8: "If you're a professional and want to use Moonlight for clients, you'll need to apply for a <a href='https://forms.moonlight.world/professional-application'>Professional account</a>.",
  },
} satisfies StringTree;

export type StringDict = typeof en;

export const strings: Record<PaywallLocale, StringTree> = {
  en,
  vi,
  "pt-BR": ptBR,
};
